/**
 * Contact-request domain — cache invalidation registry.
 *
 * Declares which queries every wrapped contact-request mutation invalidates
 * on success. Consumed by `hooks.ts` via `useInvalidatingMutation`. The
 * compiler enforces that every `ContactRequestMutationId` member has a
 * registry entry (and vice-versa), so adding a mutation without declaring its
 * invalidations is a build error.
 *
 * The per-type job creates (chain/k12/cnu/ugc) are NOT here: they run through
 * the imperative `createContactRequestJob` helper in `utils.ts` (the caller
 * picks the endpoint at submit time, so a hook can't express it), matching the
 * old hand-written behaviour.
 */

import type { QueryKey } from '@tanstack/react-query'

import { CONTACT_REQUEST_JOB_TABLE_KEY } from '../../constants/tableQueryKeys'
import {
  containsKeyFilter,
  type InvalidationRule,
} from '../invalidation/useInvalidatingMutation'
import {
  getCreditBalanceQueryKey,
  listContactRequestJobsQueryKey,
} from '../generated/hooks/contactRequestController'

// ── Mutation identifiers ────────────────────────────────────────────

/**
 * Discriminator union of every wrapped contact-request mutation. Adding a new
 * mutation requires a member here and a corresponding registry entry.
 */
export type ContactRequestMutationId =
  | 'createContactRequestJobFromCampaign'
  | 'createCreditCheckoutSession'

// ── Shared invalidation set ─────────────────────────────────────────

/**
 * Query keys a job-creating mutation should invalidate.
 *
 * Covers the generated list + credit-balance endpoints plus the two legacy
 * string-keyed caches still in active use during the migration window:
 * `['contact_requests', …]` (the hand-written infinite list) and
 * `['creditBalance']` (the hand-written `useCreditBalance`). Creating a job
 * moves credits into "pending", so the balance is invalidated too.
 * `invalidateQueries` prefix-matches, so the bare roots cover every variant.
 */
export function commonContactRequestInvalidations(): QueryKey[] {
  return [
    listContactRequestJobsQueryKey(),
    ['contact_requests'],
    getCreditBalanceQueryKey(),
    ['creditBalance'],
  ]
}

// ── Registry ────────────────────────────────────────────────────────

export const invalidationRegistry: Record<
  ContactRequestMutationId,
  InvalidationRule<any>
> = {
  createContactRequestJobFromCampaign: {
    invalidates: (variables: { campaign_id: number }) => [
      ...commonContactRequestInvalidations(),
      ['campaign', variables.campaign_id, 'has-pending-contact-request'],
      ['campaign', variables.campaign_id, 'contact-requests-count'],
    ],
    removes: () => [containsKeyFilter(CONTACT_REQUEST_JOB_TABLE_KEY)],
  },
  // Opening a Stripe checkout session doesn't change any cached data; the
  // balance updates after the webhook completes the purchase and is refreshed
  // on the success-redirect handler in the contacts page.
  createCreditCheckoutSession: {
    invalidates: () => [],
  },
}
